import dayjs, { Dayjs } from "dayjs";
import React, { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useHasPermission } from "../../store/hooks/useRbac";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { IUserFilterItem } from "../../types/user.api.types";
import {
  LeaveTypesEnum,
  MONTHS,
  TransactionMode,
} from "../../utils/constants";
import { capitalizeWords } from "../../utils/nameUtils";
import { getLeaveStatusVariant } from "../../utils/badgeVariants";
import { formatDDMMYYYY } from "../../utils/timeUtils";
import { extractApiError } from "../../utils/apiErrorUtils";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import {
  Button,
  ColumnSelector,
  DataTable,
  DataTableCard,
  DatePicker,
  EmployeeCell,
  Input,
  Modal2,
  Modal2Footer,
  Modal2Button,
  OverflowTooltip,
  PageHeader,
  Select,
  TableSkeleton,
  TextArea,
  Toggle,
} from "../common";
import { useTableConfig } from "../../hooks/useTableConfig";
import type { TableColumn } from "../common/Table";
import Badge from "../common/Badge";

// --- RTK Query Imports (contracts unchanged) ---
import {
  ILeaveCredit,
  ILeaveTransactionRequest,
  ILeaveTransactionType,
  useCreditLeaveMutation,
  useDebitLeaveMutation,
  useGetLeaveTransactionsQuery,
  ILeaveTransaction,
} from "../../store/apis/leaveCredit.api";
import { useGetMasterConfigByCategoryQuery } from "../../store/apis/masterConfig.api";
import { useGetUsersForFilterQuery } from "../../store/apis/user.api";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const LeaveCredits: React.FC = () => {
  const canManage = useHasPermission(PERMISSIONS.LEAVE_BALANCES_MANAGE);
  const breakpoint = useBreakpoint();
  const isMobile = breakpoint === "mobile";

  const [showModal, setShowModal] = useState(false);

  // RTK Mutations
  const [creditLeave, { isLoading: isCreditSubmitting }] =
    useCreditLeaveMutation();
  const [debitLeave, { isLoading: isDebitSubmitting }] =
    useDebitLeaveMutation();

  const submitting = isCreditSubmitting || isDebitSubmitting;

  const [creditPage, setCreditPage] = useState(1);
  const [creditLimit, setCreditLimit] = useState(10);

  // --- RTK Query Data Fetching (unfiltered page query; filter UI removed) ---
  const {
    data: apiResponse,
    isLoading: isTransactionsLoading,
    isFetching: isTransactionsFetching,
    isError: isTransactionsError,
    refetch: refetchTransactions,
  } = useGetLeaveTransactionsQuery({
    page: creditPage,
    limit: creditLimit,
  });

  const { data: leaveTypesData, isLoading: isLeaveTypesLoading } =
    useGetMasterConfigByCategoryQuery("leave_type");

  const { data: employeesData } = useGetUsersForFilterQuery();

  const employees: IUserFilterItem[] = useMemo(
    () => employeesData ?? [],
    [employeesData],
  );

  // Map RTK Data to Component State Structure
  const credits: ILeaveCredit[] = useMemo(() => {
    const rawData: ILeaveTransaction[] = apiResponse?.data?.data ?? [];
    // Explicitly map API items to the UI model
    return rawData.map(
      (item: ILeaveTransaction, index: number): ILeaveCredit => ({
        id: `${item.createdAt}-${index}`, // Maintain unique ID logic
        userId: item.userId,
        userName: item.userName,
        leaveType: item.leaveTypeCode,
        transactionDate: item.createdAt,
        month: item.month,
        year: item.year,
        days: item.days,
        type:
          item.type === "credit"
            ? ILeaveTransactionType.Credited
            : ILeaveTransactionType.Debited,
        credited: item.type === "credit" ? item.days : 0,
        debited: item.type === "debit" ? item.days : 0,
      }),
    );
  }, [apiResponse]);

  const creditsTotal = apiResponse?.data?.total ?? 0;

  // --- Employee & Leave-type options (backend-driven, modal use) ---
  const leaveTypeOptions = useMemo(() => {
    if (!leaveTypesData) return [];
    return leaveTypesData
      .map((f) => ({
        value: f?.id,
        label: f?.displayName || f?.filterCode,
      }))
      .filter((v) => v.value && v.label);
  }, [leaveTypesData]);

  const sortedEmployees = useMemo(
    () =>
      [...employees].sort((a, b) =>
        `${a?.fullName ?? ""}`
          .toLowerCase()
          .localeCompare(`${b?.fullName ?? ""}`.toLowerCase()),
      ),
    [employees],
  );

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const resetForm = useCallback(() => {
    setFormData({
      employeeIds: [],
      leaveType: "",
      days: 0.5,
      reason: "",
      month: currentMonth,
      year: currentYear,
    });
    setTransactionMode(TransactionMode.CREDIT);
  }, [currentMonth, currentYear]);

  const handleManageBalance = useCallback(() => {
    resetForm();
    setShowModal(true);
  }, [resetForm]);

  const closeModal = useCallback(() => {
    if (submitting) return;
    setShowModal(false);
  }, [submitting]);

  const [formData, setFormData] = useState({
    employeeIds: [] as string[],
    leaveType: "" as LeaveTypesEnum | "",
    days: 0.5 as number | string,
    reason: "",
    month: currentMonth as number | null,
    year: currentYear as number | null,
  });
  const [transactionMode, setTransactionMode] = useState<TransactionMode>(
    TransactionMode.CREDIT,
  );

  const daysValue =
    typeof formData.days === "string"
      ? Number.parseFloat(formData.days) || 0
      : formData.days;
  const isDaysLimitExceeded = daysValue > 365;
  const daysError = isDaysLimitExceeded
    ? "Leave days cannot exceed 365."
    : "";

  const validateDays = useCallback((value: number | string): boolean => {
    const numeric =
      typeof value === "string" ? Number.parseFloat(value) || 0 : value;
    if (value === "" || numeric < 0.5) {
      toast.error("Please fill in all required fields with valid values.");
      return false;
    }
    if (numeric % 0.5 !== 0) {
      toast.error(
        "Days must be in increments of 0.5 (e.g., 0.5, 1.0, 1.5, 2.0, 2.5).",
      );
      return false;
    }
    return true;
  }, []);

  const handleCredit = useCallback(async () => {
    if (
      formData.employeeIds.length === 0 ||
      !formData.leaveType ||
      !formData.month ||
      !formData.year
    ) {
      toast.error("Please fill in all required fields with valid values.");
      return;
    }
    if (!validateDays(formData.days)) return;

    try {
      const payloadBase: ILeaveTransactionRequest = {
        userIds: formData.employeeIds, // Send all IDs at once
        leaveType: formData.leaveType,
        credited: daysValue, // DTO requires 'credited'
        month: formData.month,
        year: formData.year,
        reason: formData.reason,
        creditedOn: new Date().toISOString(), // Optional
      };

      await creditLeave(payloadBase).unwrap();
      toast.success(
        `${formData.employeeIds.length} Leave balance credited successfully!`,
      );
      setCreditPage(1); // Reset to page 1 to see new data
      setShowModal(false);
    } catch (err: unknown) {
      toast.error(extractApiError(err) || "Failed to credit leave balance.");
    }
  }, [formData, daysValue, validateDays, creditLeave]);

  const handleDebit = useCallback(async () => {
    if (
      formData.employeeIds.length === 0 ||
      !formData.leaveType ||
      !formData.month ||
      !formData.year
    ) {
      toast.error("Please fill in all required fields with valid values.");
      return;
    }
    if (!validateDays(formData.days)) return;

    try {
      const payloadBase: ILeaveTransactionRequest = {
        userIds: formData.employeeIds, // Send all IDs at once
        leaveType: formData.leaveType,
        debited: daysValue,
        month: formData.month,
        year: formData.year,
        reason: formData.reason,
        debitedOn: new Date().toISOString(),
      };

      await debitLeave(payloadBase).unwrap();
      toast.success(
        `${formData.employeeIds.length} Leave balance debited successfully!`,
      );
      setCreditPage(1);
      setShowModal(false);
    } catch (err: unknown) {
      toast.error(extractApiError(err) || "Failed to debit leave balance.");
    }
  }, [formData, daysValue, validateDays, debitLeave]);

  const handleSave = useCallback(() => {
    if (transactionMode === TransactionMode.CREDIT) {
      void handleCredit();
    } else {
      void handleDebit();
    }
  }, [transactionMode, handleCredit, handleDebit]);

  const handleClearEmployees = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      employeeIds: [],
      leaveType: "",
      month: null,
      year: null,
      days: "",
      reason: "",
    }));
    setTransactionMode(TransactionMode.CREDIT);
  }, []);

  const handleSelectAllEmployees = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      employeeIds: employees.map((emp) => emp?.id),
    }));
  }, [employees]);

  const handlePageChange = useCallback((page: number) => {
    setCreditPage(page);
  }, []);

  const handlePageSizeChange = useCallback((pageSize: number) => {
    setCreditLimit(pageSize);
    setCreditPage(1);
  }, []);

  const allLeaveCreditColumns: TableColumn<ILeaveCredit>[] = useMemo(
    () => [
      {
        key: "employee",
        title: "Employee",
        required: true,
        width: 220,
        render: (_, r) => (
          <EmployeeCell name={r?.userName ?? "Unknown"} />
        ),
      },
      {
        key: "leave_type",
        title: "Leave Type",
        width: 150,
        render: (_, r) => {
          const matchedConfig = leaveTypesData?.find(
            (type: { id?: string; filterCode?: string }) =>
              type.id === r.leaveType || type.filterCode === r.leaveType,
          );
          const label = matchedConfig?.displayName || r.leaveType || "-";
          return (
            <Badge variant={getLeaveStatusVariant(r.leaveType)}>
              {label}
            </Badge>
          );
        },
      },
      {
        key: "dates",
        title: "Month/Year",
        width: 140,
        render: (_, r) =>
          r.month && r.year ? (
            <OverflowTooltip className="block truncate text-body-sm text-text-secondary">
              {`${MONTHS[r.month - 1]?.label || ""} ${r.year}`}
            </OverflowTooltip>
          ) : (
            "N/A"
          ),
      },
      {
        key: "type",
        title: "Type",
        width: 110,
        render: (_, r) => (
          <span className="text-body-sm font-semibold text-text-secondary">
            {capitalizeWords(r.type || ILeaveTransactionType.Credited)}
          </span>
        ),
      },
      {
        key: "days",
        title: "Days",
        width: 90,
        render: (_, r) => {
          const days =
            typeof r.days === "number"
              ? r.days
              : r.type === ILeaveTransactionType.Debited
                ? (r.debited ?? 0)
                : (r.credited ?? 0);
          const isDebit = r.type === ILeaveTransactionType.Debited;
          return (
            <span className="text-body-sm tabular-nums text-text-primary">
              {isDebit ? `- ${days}` : `+ ${days}`}
            </span>
          );
        },
      },
      {
        key: "transaction_date",
        title: "Date",
        width: 130,
        render: (_, r) => (
          <span className="text-body-sm text-text-secondary">
            {formatDDMMYYYY(r.transactionDate) ?? "-"}
          </span>
        ),
      },
    ],
    [leaveTypesData],
  );

  const { configuredColumns, visibleKeys, toggleVisibility } = useTableConfig(
    allLeaveCreditColumns,
    { persistenceKey: "leave-credits-table" },
  );

  const table = (
    <DataTable
      columns={configuredColumns}
      rows={credits}
      rowKey={(r) => r.id}
      loading={isTransactionsLoading}
      fetching={isTransactionsFetching}
      skeleton={<TableSkeleton rows={8} columns={6} />}
      emptyMessage="No leave transactions found. Credited or debited balances will appear here."
      responsive="scroll"
      minWidth={840}
      pagination={{
        total: creditsTotal,
        page: creditPage,
        pageSize: creditLimit,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        autoHide: true,
      }}
    />
  );

  return (
    <div className="w-full animate-fade-in">
      <PageHeader
        title="Leave Balance"
        description="Credit or debit employee leave balances"
        breadcrumbs={[
          { label: "Home", path: "/dashboard" },
          { label: "Leave Balance" },
        ]}
        actions={
          canManage && (
            <Button
              appearance="primary"
              size="middle"
              onClick={handleManageBalance}
            >
              Manage Balance
            </Button>
          )
        }
      />

      {!canManage && (
        <div className="mb-5 rounded-card border border-border-secondary bg-surface px-5 py-4 shadow-soft">
          <p className="text-body-sm text-text-secondary">
            You have view-only access to leave balances. Contact an admin to
            manage balances.
          </p>
        </div>
      )}

      {isTransactionsError ? (
        <div className="rounded-card border border-border-secondary bg-surface px-6 py-14 text-center shadow-soft">
          <h3 className="mb-1 text-heading-sm text-text-primary">
            Failed to load leave transactions
          </h3>
          <p className="mx-auto mb-5 max-w-md text-body-sm text-text-secondary">
            Something went wrong while fetching leave transactions. Please try
            again.
          </p>
          <Button
            appearance="primary"
            size="middle"
            onClick={() => refetchTransactions()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <DataTableCard
          title="Leave Transactions"
          count={creditsTotal}
          toolbar={
            <ColumnSelector
              columns={allLeaveCreditColumns}
              visibleKeys={visibleKeys}
              onToggle={toggleVisibility}
            />
          }
        >
          {table}
        </DataTableCard>
      )}

      <Modal2
        isOpen={showModal}
        onClose={closeModal}
        title="Manage Leave Balance"
        subtitle="Credit or debit leave days for one or more employees"
        size="lg"
        footer={
          <Modal2Footer justify="end" className="flex-wrap">
            <Modal2Button
              variant="secondary"
              onClick={closeModal}
              disabled={submitting}
            >
              Cancel
            </Modal2Button>
            <Modal2Button
              variant="primary"
              onClick={handleSave}
              disabled={submitting || isDaysLimitExceeded}
            >
              Save
            </Modal2Button>
          </Modal2Footer>
        }
      >
        <div className="space-y-4">
          <div>
            <Select
              label="Employee(s)"
              required
              multiple
              searchable
              value={formData.employeeIds}
              onChange={(value) =>
                setFormData({
                  ...formData,
                  employeeIds: Array.isArray(value)
                    ? (value as string[])
                    : [],
                })
              }
              options={sortedEmployees.map((employee) => ({
                value: employee?.id,
                label: capitalizeWords(employee?.fullName ?? ""),
              }))}
              placeholder="Select employees"
            />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <Button
                  appearance="link"
                  size="small"
                  onClick={handleSelectAllEmployees}
                >
                  Select All
                </Button>
                <Button
                  appearance="link"
                  size="small"
                  onClick={handleClearEmployees}
                >
                  Clear All
                </Button>
              </div>
              {formData.employeeIds.length > 0 && (
                <span className="text-body-xs font-semibold text-primary-700">
                  {formData.employeeIds.length} employee(s) selected
                </span>
              )}
            </div>
          </div>

          <Select
            label="Leave Type"
            required
            searchable
            value={formData.leaveType}
            loading={isLeaveTypesLoading}
            onChange={(value) =>
              setFormData({
                ...formData,
                leaveType: value as LeaveTypesEnum,
              })
            }
            options={leaveTypeOptions.map((type) => ({
              value: type.value,
              label: type.label,
            }))}
            placeholder="Select Leave Type"
          />

          <DatePicker
            label="Month & Year"
            required
            allowClear={!isMobile}
            picker="month"
            format="MMMM YYYY"
            value={
              formData.month && formData.year
                ? dayjs()
                    .year(formData.year)
                    .month(formData.month - 1)
                : null
            }
            onChange={(date: Dayjs | null) => {
              if (date) {
                setFormData({
                  ...formData,
                  month: date.month() + 1, // dayjs month is 0-indexed
                  year: date.year(),
                });
              } else {
                setFormData({
                  ...formData,
                  month: null,
                  year: null,
                });
              }
            }}
            placeholder="Select month and year"
          />

          <div>
            <span className="mb-2 block text-label font-semibold text-text-primary">
              Mode <span className="text-error">*</span>
            </span>
            <div className="flex flex-nowrap items-center gap-3">
              <span
                className={`shrink-0 whitespace-nowrap text-body-sm font-semibold ${transactionMode === TransactionMode.CREDIT ? "text-primary-700" : "text-text-tertiary"}`}
              >
                Credit
              </span>
              <Toggle
                checked={transactionMode === TransactionMode.DEBIT}
                onChange={(checked) =>
                  setTransactionMode(
                    checked ? TransactionMode.DEBIT : TransactionMode.CREDIT,
                  )
                }
                size="md"
                label="Transaction mode: credit or debit"
                className="flex-none"
              />
              <span
                className={`shrink-0 whitespace-nowrap text-body-sm font-semibold ${transactionMode === TransactionMode.DEBIT ? "text-error-dark" : "text-text-tertiary"}`}
              >
                Debit
              </span>
            </div>
          </div>

          <Input
            type="number"
            label="Days"
            required
            value={formData.days}
            min={0.5}
            max={365}
            step={0.5}
            onChange={(value) =>
              setFormData({ ...formData, days: value as number | string })
            }
            placeholder="Enter days (e.g., 2.5)"
            error={daysError}
            helperText="Must be in increments of 0.5 (e.g., 0.5, 1.0, 1.5)"
          />

          <TextArea
            label="Reason"
            value={formData.reason}
            onChange={(value) =>
              setFormData({ ...formData, reason: value as string })
            }
            placeholder="Optional reason"
          />
        </div>
      </Modal2>
    </div>
  );
};

export default LeaveCredits;
